import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, InputNumber, Button, Row, Col, DatePicker, message, Space, Divider } from 'antd';
import { EnvironmentOutlined, CalendarOutlined, SaveOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import YandexMap from './YandexMap';
import AddressAutocomplete from './AddressAutocomplete';
import { AddressSuggestion } from '../../services/addressService';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';

// TextArea was considered but not used; keep comment to avoid removing for future UX

interface LocationModalProps {
  visible: boolean;
  onCancel: () => void;
  onSave: (locationData: {
    latitude: number;
    longitude: number;
    address?: string;
    recordedAt: Date;
  }, cargoId?: number) => void;
  initialLocation?: {
    latitude: number;
    longitude: number;
    address?: string;
    recordedAt?: Date;
  };
  routeId?: number;
  cargoId?: number;
  title?: string;
}

const LocationModal: React.FC<LocationModalProps> = ({
  visible,
  onCancel,
  onSave,
  initialLocation,
  routeId,
  cargoId,
  title = 'Местоположение груза'
}) => {
  const [form] = Form.useForm();
  const [selectedCoordinates, setSelectedCoordinates] = useState<[number, number] | null>(
    initialLocation ? [initialLocation.latitude, initialLocation.longitude] : null
  );
  const [selectedAddress, setSelectedAddress] = useState<string | undefined>(
    initialLocation?.address
  );
  const [isLoading, setIsLoading] = useState(false);

  // Сброс формы при открытии/закрытии модального окна
  useEffect(() => {
    if (visible) {
      if (initialLocation) {
        form.setFieldsValue({
          latitude: Number(initialLocation.latitude),
          longitude: Number(initialLocation.longitude),
          coordinatesString: `${initialLocation.latitude}, ${initialLocation.longitude}`,
          address: initialLocation.address,
          recordedAt: initialLocation.recordedAt ? dayjs(initialLocation.recordedAt) : dayjs()
        });
        setSelectedCoordinates([initialLocation.latitude, initialLocation.longitude]);
        setSelectedAddress(initialLocation.address);
      } else {
        form.resetFields();
        form.setFieldsValue({
          recordedAt: dayjs()
        });
        setSelectedCoordinates(null);
        setSelectedAddress(undefined);
      }
    }
  }, [visible, initialLocation, form]);

  // Обработчик клика по карте
  const handleMapClick = (coordinates: [number, number]) => {
    console.log('📍 LocationModal: получены координаты от карты:', coordinates);
    setSelectedCoordinates(coordinates);
    form.setFieldsValue({
      latitude: Number(coordinates[0]),
      longitude: Number(coordinates[1]),
      coordinatesString: `${coordinates[0]}, ${coordinates[1]}`
    });
    
    // Автоматически получаем адрес по координатам
    getAddressFromCoordinates(coordinates);
  };

  // Получение адреса по координатам через обратное геокодирование
  const getAddressFromCoordinates = async (coordinates: [number, number]): Promise<string | undefined> => {
    try {
      // Сначала пытаемся использовать backend endpoint
      const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const backendUrl = `${API_BASE_URL}/address/reverse?lat=${coordinates[0]}&lng=${coordinates[1]}`;
      
      try {
        const backendResponse = await fetch(backendUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (backendResponse.ok) {
          const backendData = await backendResponse.json();
          if (backendData.address) {
            setSelectedAddress(backendData.address);
            form.setFieldsValue({ address: backendData.address });
            return backendData.address;
          }
        }
      } catch (backendError) {
        console.warn('Backend reverse geocoding failed, trying direct API:', backendError);
      }

      // Fallback: прямой доступ к Яндекс.Карт API (если backend недоступен)
      const apiKey = process.env.REACT_APP_YANDEX_API_KEY;
      if (!apiKey) {
        console.warn('API ключ Яндекс.Карт не найден (ни в backend, ни в frontend)');
        return undefined;
      }

      const response = await fetch(
        `https://geocode-maps.yandex.ru/1.x/?apikey=${apiKey}&format=json&geocode=${coordinates[1]},${coordinates[0]}&lang=ru_RU&results=1`
      );

      if (response.ok) {
        const data = await response.json();
        const address = data.response?.GeoObjectCollection?.featureMember?.[0]?.GeoObject?.metaDataProperty?.GeocoderMetaData?.text;
        
        if (address) {
          setSelectedAddress(address);
          form.setFieldsValue({ address });
          return address;
        }
      }
      return undefined;
    } catch (error) {
      console.error('Ошибка получения адреса по координатам:', error);
      return undefined;
    }
  };

  // Обработчик выбора адреса из автодополнения
  const handleAddressSelect = (value: string, suggestion?: AddressSuggestion) => {
    setSelectedAddress(value);
    
    if (suggestion?.coordinates) {
      const coords: [number, number] = [suggestion.coordinates.lat, suggestion.coordinates.lng];
      setSelectedCoordinates(coords);
      form.setFieldsValue({
        latitude: Number(coords[0]),
        longitude: Number(coords[1]),
        coordinatesString: `${coords[0]}, ${coords[1]}`
      });
    }
  };

  // Обработчик отправки формы
  const handleSubmit = async () => {
    try {
      console.log('🚀 Начинаем сохранение местоположения...');
      const values = await form.validateFields();
      console.log('📋 Значения формы:', values);
      
      let latitude: number;
      let longitude: number;
      
      // Приоритет отдается строке координат, если она заполнена
      if (values.coordinatesString) {
        const coords = values.coordinatesString.split(',').map((s: string) => s.trim());
        latitude = parseFloat(coords[0]);
        longitude = parseFloat(coords[1]);
        console.log('📍 Координаты из строки:', { latitude, longitude });
      } else {
        // Используем отдельные поля, если строка не заполнена
        latitude = Number(values.latitude);
        longitude = Number(values.longitude);
        console.log('📍 Координаты из отдельных полей:', { latitude, longitude });
      }
      
      if (isNaN(latitude) || isNaN(longitude)) {
        message.error('Введите корректные координаты');
        return;
      }
      
      if (!selectedCoordinates) {
        message.error('Выберите местоположение на карте или введите координаты');
        return;
      }

      setIsLoading(true);

      // Получаем адрес по координатам, если он не указан
      let address = values.address;
      if (!address && selectedCoordinates) {
        try {
          console.log('🔍 Получаем адрес по координатам:', selectedCoordinates);
          address = await getAddressFromCoordinates(selectedCoordinates);
          console.log('📍 Полученный адрес:', address);
        } catch (error) {
          console.log('⚠️ Не удалось получить адрес по координатам:', error);
          // Продолжаем без адреса
        }
      }

      const locationData = {
        latitude: latitude,
        longitude: longitude,
        address: address,
        recordedAt: values.recordedAt.toDate()
      };

      console.log('📤 Отправляем данные:', locationData);
      console.log('📤 routeId:', routeId, 'cargoId:', cargoId);

      await onSave(locationData, cargoId);
      message.success('Местоположение сохранено');
      onCancel();
    } catch (error) {
      console.error('❌ Ошибка сохранения местоположения:', error);
      message.error('Ошибка сохранения местоположения');
    } finally {
      setIsLoading(false);
    }
  };

  // Маркеры для карты
  const markers = selectedCoordinates ? [{
    id: 'selected',
    position: selectedCoordinates,
    title: 'Выбранное местоположение',
    description: selectedAddress || 'Координаты выбраны',
    color: 'blue'
  }] : [];

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      destroyOnClose={true}
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Координаты (в одном поле)"
              required
            >
              <Form.Item
                name="coordinatesString"
                rules={[
                  { required: true, message: 'Введите координаты' },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      
                      const coords = value.split(',').map((s: string) => s.trim());
                      if (coords.length !== 2) {
                        return Promise.reject(new Error('Формат: широта, долгота (например: 55.158253, 82.970053)'));
                      }
                      
                      const lat = parseFloat(coords[0]);
                      const lng = parseFloat(coords[1]);
                      
                      if (isNaN(lat) || isNaN(lng)) {
                        return Promise.reject(new Error('Координаты должны быть числами'));
                      }
                      
                      if (lat < -90 || lat > 90) {
                        return Promise.reject(new Error('Широта должна быть от -90 до 90'));
                      }
                      
                      if (lng < -180 || lng > 180) {
                        return Promise.reject(new Error('Долгота должна быть от -180 до 180'));
                      }
                      
                      return Promise.resolve();
                    }
                  }
                ]}
                noStyle
              >
                <Input
                  placeholder="55.158253, 82.970053"
                  prefix={<EnvironmentOutlined />}
                  onChange={async (e) => {
                    const value = e.target.value;
                    if (value) {
                      const coords = value.split(',').map((s: string) => s.trim());
                      if (coords.length === 2) {
                        const lat = parseFloat(coords[0]);
                        const lng = parseFloat(coords[1]);
                        
                        if (!isNaN(lat) && !isNaN(lng) && 
                            lat >= -90 && lat <= 90 && 
                            lng >= -180 && lng <= 180) {
                          setSelectedCoordinates([lat, lng]);
                          form.setFieldsValue({
                            latitude: lat,
                            longitude: lng
                          });
                          
                          // Автоматически получаем адрес по координатам
                          try {
                            const address = await getAddressFromCoordinates([lat, lng]);
                            if (address) {
                              form.setFieldsValue({ address });
                              setSelectedAddress(address);
                            }
                          } catch (error) {
                            console.log('⚠️ Не удалось получить адрес по координатам:', error);
                          }
                        }
                      }
                    }
                  }}
                />
              </Form.Item>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Координаты (по отдельности)"
            >
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item
                    name="latitude"
                    rules={[
                      { type: 'number', min: -90, max: 90, message: 'Широта должна быть от -90 до 90' }
                    ]}
                    noStyle
                  >
                    <InputNumber
                      placeholder="Широта"
                      prefix={<EnvironmentOutlined />}
                      min={-90}
                      max={90}
                      step={0.000001}
                      precision={6}
                      style={{ width: '100%' }}
                      onChange={(value) => {
                        if (value !== null && typeof value === 'number') {
                          const lng = form.getFieldValue('longitude');
                          if (lng !== undefined && typeof lng === 'number') {
                            setSelectedCoordinates([value, lng]);
                            // Обновляем строку координат
                            form.setFieldsValue({
                              coordinatesString: `${value}, ${lng}`
                            });
                          }
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="longitude"
                    rules={[
                      { type: 'number', min: -180, max: 180, message: 'Долгота должна быть от -180 до 180' }
                    ]}
                    noStyle
                  >
                    <InputNumber
                      placeholder="Долгота"
                      prefix={<EnvironmentOutlined />}
                      min={-180}
                      max={180}
                      step={0.000001}
                      precision={6}
                      style={{ width: '100%' }}
                      onChange={(value) => {
                        if (value !== null && typeof value === 'number') {
                          const lat = form.getFieldValue('latitude');
                          if (lat !== undefined && typeof lat === 'number') {
                            setSelectedCoordinates([lat, value]);
                            // Обновляем строку координат
                            form.setFieldsValue({
                              coordinatesString: `${lat}, ${value}`
                            });
                          }
                        }
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Дата и время"
              name="recordedAt"
              rules={[{ required: true, message: 'Выберите дату и время' }]}
            >
              <DatePicker
                showTime
                format="DD.MM.YYYY HH:mm"
                placeholder="Выберите дату и время"
                style={{ width: '100%' }}
                prefix={<CalendarOutlined />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Адрес"
          name="address"
        >
          <AddressAutocomplete
            placeholder="Введите адрес или выберите на карте"
            onChange={handleAddressSelect}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Divider>Карта</Divider>
        
        <div style={{ marginBottom: 16 }}>
          <p style={{ margin: 0, color: '#666' }}>
            Кликните по карте для выбора местоположения или введите координаты вручную
          </p>
        </div>

        <YandexMap
          center={selectedCoordinates || [55.7558, 37.6176]}
          zoom={selectedCoordinates ? 15 : 10}
          markers={markers}
          onMapClick={handleMapClick}
          height={400}
          style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}
        />

        <Divider />

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>
              Отмена
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSubmit}
              loading={isLoading}
              disabled={!selectedCoordinates}
            >
              Сохранить
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LocationModal; 